import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Inventory } from "../../../../../api";

import styles from "./InventoryMovementsPage.module.scss";
import {
  LIMIT_OPTIONS,
  MOVEMENT_TYPE_OPTIONS,
  formatCurrency,
  formatDateTime,
  formatSaleDate,
  formatSignedQuantity,
  getMovementBadgeClass,
  getMovementDirection,
  getMovementTypeLabel,
  getMovementUserName,
  getMovementVariant,
  getMovementsResponse,
  getProductFilterOptions,
  getProductImage,
  getVariantLabel,
} from "./InventoryMovementsPage.form";

function getExistencesResponse(result) {
  const response = result?.existences || result?.response || result || {};

  if (Array.isArray(response.docs)) {
    return response;
  }

  if (Array.isArray(response)) {
    return { docs: response };
  }

  return { docs: [] };
}

export function InventoryMovementsPage() {
  const [movements, setMovements] = useState([]);

  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  });

  const [referenceValue, setReferenceValue] = useState("");
  const [appliedReference, setAppliedReference] = useState("");

  const [typeFilter, setTypeFilter] = useState("");
  const [productFilter, setProductFilter] = useState("");
  const [dateFromFilter, setDateFromFilter] = useState("");
  const [dateToFilter, setDateToFilter] = useState("");

  const [productOptions, setProductOptions] = useState([]);

  const [limit, setLimit] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");

  const loadMovements = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      const inventoryController = new Inventory();

      const result = await inventoryController.getMovements(
        currentPage,
        limit,
        {
          type: typeFilter || undefined,
          product: productFilter || undefined,
          dateFrom: dateFromFilter || undefined,
          dateTo: dateToFilter || undefined,
          reference: appliedReference || undefined,
        }
      );

      const response = getMovementsResponse(result);

      setMovements(response.docs || []);

      setPagination({
        totalDocs: Number(response.totalDocs) || 0,
        totalPages: Math.max(Number(response.totalPages) || 1, 1),
        page: Number(response.page) || currentPage,
        limit: Number(response.limit) || limit,
      });
    } catch (errorResponse) {
      console.error("loadMovements error:", errorResponse);

      setMovements([]);

      setPagination({
        totalDocs: 0,
        totalPages: 1,
        page: 1,
        limit,
      });

      setError(
        errorResponse?.msg ||
          "No fue posible cargar el Kardex. Intenta nuevamente."
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    appliedReference,
    currentPage,
    dateFromFilter,
    dateToFilter,
    limit,
    productFilter,
    typeFilter,
  ]);

  useEffect(() => {
    loadMovements();
  }, [loadMovements]);

  useEffect(() => {
    async function loadProductOptions() {
      try {
        const inventoryController = new Inventory();
        const result = await inventoryController.getExistences(1, 100, {});
        const response = getExistencesResponse(result);

        setProductOptions(getProductFilterOptions(response.docs || []));
      } catch (errorResponse) {
        console.error("loadProductOptions error:", errorResponse);

        setProductOptions([]);
      }
    }

    loadProductOptions();
  }, []);

  const firstVisibleItem = useMemo(() => {
    if (pagination.totalDocs === 0) {
      return 0;
    }

    return (pagination.page - 1) * pagination.limit + 1;
  }, [pagination]);

  const lastVisibleItem = useMemo(() => {
    return Math.min(pagination.page * pagination.limit, pagination.totalDocs);
  }, [pagination]);

  const hasFilters = Boolean(
    appliedReference || typeFilter || productFilter || dateFromFilter || dateToFilter
  );

  function handleReferenceSubmit(event) {
    event.preventDefault();
    setCurrentPage(1);
    setAppliedReference(referenceValue.trim());
  }

  function handleTypeFilterChange(event) {
    setTypeFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleProductFilterChange(event) {
    setProductFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleDateFromChange(event) {
    setDateFromFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleDateToChange(event) {
    setDateToFilter(event.target.value);
    setCurrentPage(1);
  }

  function handleClearFilters() {
    setReferenceValue("");
    setAppliedReference("");
    setTypeFilter("");
    setProductFilter("");
    setDateFromFilter("");
    setDateToFilter("");
    setCurrentPage(1);
  }

  function handleLimitChange(event) {
    setLimit(Number(event.target.value));
    setCurrentPage(1);
  }

  return (
    <section className={styles.MovementsPage}>
      <header className={styles.PageHeader}>
        <div>
          <p className={styles.Breadcrumb}>Inventario / Kardex</p>

          <h1>Kardex de movimientos</h1>

          <p className={styles.Subtitle}>
            Historial completo de entradas, salidas y ajustes de inventario,
            con quién hizo cada movimiento y cuándo.
          </p>
        </div>

        <div className={styles.HeaderActions}>
          <Link to="/admin/inventory" className={styles.BackButton}>
            ← Volver a Inventario
          </Link>
        </div>
      </header>

      <section className={styles.MovementsCard}>
        <div className={styles.FiltersBar}>
          <form
            className={styles.SearchForm}
            onSubmit={handleReferenceSubmit}
          >
            <input
              type="search"
              value={referenceValue}
              onChange={(event) => setReferenceValue(event.target.value)}
              placeholder="Buscar por referencia..."
              aria-label="Buscar por referencia"
            />

            <button type="submit" aria-label="Buscar">
              ⌕
            </button>
          </form>

          <label className={styles.FilterControl}>
            <span>Tipo de movimiento</span>

            <select value={typeFilter} onChange={handleTypeFilterChange}>
              <option value="">Todos</option>

              {MOVEMENT_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className={styles.FilterControl}>
            <span>Producto</span>

            <select
              value={productFilter}
              onChange={handleProductFilterChange}
            >
              <option value="">Todos</option>

              {productOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <div className={styles.DateRangeControl}>
            <span>Rango de fechas</span>

            <div className={styles.DateRangeInputs}>
              <input
                type="date"
                value={dateFromFilter}
                onChange={handleDateFromChange}
                aria-label="Desde"
              />

              <span>—</span>

              <input
                type="date"
                value={dateToFilter}
                onChange={handleDateToChange}
                aria-label="Hasta"
              />
            </div>
          </div>

          <button
            type="button"
            className={styles.RefreshButton}
            onClick={loadMovements}
            disabled={isLoading}
          >
            {isLoading ? "Actualizando..." : "Actualizar"}
          </button>

          {hasFilters && (
            <button
              type="button"
              className={styles.ClearFiltersButton}
              onClick={handleClearFilters}
            >
              Limpiar filtros
            </button>
          )}
        </div>

        {error && (
          <div className={styles.ErrorMessage} role="alert">
            {error}
          </div>
        )}

        {isLoading ? (
          <div className={styles.StateMessage}>Cargando movimientos...</div>
        ) : movements.length === 0 ? (
          <div className={styles.EmptyState}>
            <p>
              {hasFilters
                ? "No se encontraron movimientos con los filtros seleccionados."
                : "Todavía no hay movimientos de inventario registrados."}
            </p>

            {hasFilters && (
              <button
                type="button"
                className={styles.ClearFiltersButton}
                onClick={handleClearFilters}
              >
                Limpiar filtros
              </button>
            )}
          </div>
        ) : (
          <>
            <div className={styles.TableContainer}>
              <table className={styles.Table}>
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Producto</th>
                    <th>Tipo</th>
                    <th>Cantidad</th>
                    <th>Stock (antes → después)</th>
                    <th>Costo unit.</th>
                    <th>Referencia</th>
                    <th>Realizado por</th>
                  </tr>
                </thead>

                <tbody>
                  {movements.map((movement) => {
                    const variant = getMovementVariant(movement);
                    const direction = getMovementDirection(movement.type);

                    return (
                      <tr key={movement._id}>
                        <td>
                          <span className={styles.DateCell}>
                            {formatDateTime(movement.createdAt)}

                            {movement.saleDate && (
                              <small>
                                Venta: {formatSaleDate(movement.saleDate)}
                              </small>
                            )}
                          </span>
                        </td>

                        <td>
                          <div className={styles.ProductCell}>
                            {getProductImage(movement.product) ? (
                              <img
                                className={styles.ProductImage}
                                src={getProductImage(movement.product)}
                                alt=""
                              />
                            ) : (
                              <span
                                className={styles.ProductImagePlaceholder}
                              >
                                ▣
                              </span>
                            )}

                            <div>
                              <strong
                                title={
                                  movement.product?.name ||
                                  "Producto eliminado"
                                }
                              >
                                {movement.product?.name || "Producto eliminado"}
                              </strong>

                              <span
                                className={styles.SkuCode}
                                title={variant?.sku || ""}
                              >
                                {variant?.sku || "—"} ·{" "}
                                {getVariantLabel(variant)}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <span
                            className={getMovementBadgeClass(
                              styles,
                              movement.type
                            )}
                          >
                            {getMovementTypeLabel(movement.type)}
                          </span>
                        </td>

                        <td>
                          <span
                            className={styles.QuantityValue}
                            data-direction={direction}
                          >
                            {formatSignedQuantity(movement)}
                          </span>
                        </td>

                        <td>
                          <span className={styles.StockRange}>
                            {movement.stockBefore} →{" "}
                            <strong>{movement.stockAfter}</strong>
                          </span>
                        </td>

                        <td>{formatCurrency(movement.unitCost)}</td>

                        <td>
                          <span
                            className={styles.ReferenceValue}
                            title={movement.reference || ""}
                          >
                            {movement.reference || "—"}
                          </span>
                        </td>

                        <td>
                          <span
                            className={styles.UserValue}
                            title={getMovementUserName(movement)}
                          >
                            {getMovementUserName(movement)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <footer className={styles.Pagination}>
              <span className={styles.TableInfo}>
                Mostrando {firstVisibleItem} a {lastVisibleItem} de{" "}
                {pagination.totalDocs} movimientos
              </span>

              <div className={styles.PageControls}>
                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) => Math.max(page - 1, 1))
                  }
                  disabled={currentPage === 1}
                  aria-label="Página anterior"
                >
                  ‹
                </button>

                <span className={styles.CurrentPage}>
                  {pagination.page} de {pagination.totalPages}
                </span>

                <button
                  type="button"
                  className={styles.PageButton}
                  onClick={() =>
                    setCurrentPage((page) =>
                      Math.min(page + 1, pagination.totalPages)
                    )
                  }
                  disabled={currentPage === pagination.totalPages}
                  aria-label="Página siguiente"
                >
                  ›
                </button>
              </div>

              <label className={styles.LimitControl}>
                <select value={limit} onChange={handleLimitChange}>
                  {LIMIT_OPTIONS.map((optionLimit) => (
                    <option key={optionLimit} value={optionLimit}>
                      {optionLimit} por página
                    </option>
                  ))}
                </select>
              </label>
            </footer>
          </>
        )}
      </section>
    </section>
  );
}
